import React, {forwardRef, MouseEvent, PropsWithChildren} from 'react';

import {
  annotateTaskType,
  TaskTypesEnum,
} from '../../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {InteractionTypesEnum} from '../../../../libs/shared/helpers/src/interaction/interactionTypes';
import {useInteractionContext} from '../../../../libs/shared/ui/src/interactionTracking/interactionContext';
import {useInteractionTracking} from '../../../../libs/shared/ui/src/interactionTracking/interactionTrackingContext';
import {DefaultLink, DefaultLinkProps} from './defaultLink';

/*
 * Props.
 */

export type LinkProps = PropsWithChildren<DefaultLinkProps & InteractionComponentProps>;

/*
 * Component.
 */

export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {interactionId, ...defaultLinkProps} = props;
  const interactionContext = useInteractionContext();
  const {trackComponentInteraction} = useInteractionTracking();

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    annotateTaskType(TaskTypesEnum.CLICK_EVENT);

    if (props.onClick) {
      props.onClick(event);
    }

    trackComponentInteraction(InteractionTypesEnum.CLICK, interactionContext, props, event);
  };

  const onDoubleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (props.onDoubleClick) {
      props.onDoubleClick(event);
    }

    trackComponentInteraction(InteractionTypesEnum.DOUBLE_CLICK, interactionContext, props, event);
  };

  return (
    <DefaultLink
      {...defaultLinkProps}
      ref={ref}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      // Browsers make links draggable by default, so make it explicitly "false" unless told otherwise.
      draggable={Boolean(props.draggable)}
      onDragStart={(e) => {
        if (props.draggable) {
          return;
        }

        // Firefox doesn't respect draggable=false on links so we need to do this to properly disable link dragging...
        // https://stackoverflow.com/questions/26356877/html5-draggable-false-not-working-in-firefox-browser
        e.preventDefault();
      }}
      {...buildInteractionIdProps(props.interactionId)}
    >
      {props.children}
    </DefaultLink>
  );
});
